import { Button, Spinner, Tabs } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { useCallback, useEffect, useState } from 'react'
import { useFetchLedger, useFetchPayoutsLedger, type LedgerEntry, type LedgerKind } from '../api'
import { LedgerList } from './LedgerList'

type TabValue = 'ALL' | 'EARNINGS' | 'PAYOUTS' | 'OTHER'

const TAB_KIND_MAP: Record<Exclude<TabValue, 'ALL' | 'PAYOUTS'>, LedgerKind> = {
  EARNINGS: 'EARNING_CREDIT',
  OTHER: 'ADJUSTMENT'
}

const PAGE_LIMIT = 8

type TabChildProps = {
  onSeenAdjustment: () => void
}

type LedgerTabProps = TabChildProps & {
  kind?: LedgerKind
}

const LedgerTab = ({ kind, onSeenAdjustment }: LedgerTabProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pages, setPages] = useState<LedgerEntry[][]>([])
  const query = useFetchLedger({ kind, page: currentPage, limit: PAGE_LIMIT })

  useEffect(() => {
    if (!query.data) return
    const pageData = query.data.data
    setPages((prev) => {
      const next = [...prev]
      next[currentPage - 1] = pageData
      return next
    })
    if (pageData.some((row) => row.kind === 'ADJUSTMENT')) {
      onSeenAdjustment()
    }
  }, [query.data, currentPage, onSeenAdjustment])

  const entries = pages.flat()
  const hasMore = query.data ? query.data.meta.total > query.data.meta.page * query.data.meta.limit : false
  const isFirstLoad = pages.length === 0 && query.isLoading

  return (
    <TabBody
      entries={entries}
      isFirstLoad={isFirstLoad}
      isFetchingMore={query.isFetching && currentPage > pages.length}
      hasMore={hasMore}
      onLoadMore={() => setCurrentPage((p) => p + 1)}
    />
  )
}

const PayoutsTab = ({ onSeenAdjustment: _onSeenAdjustment }: TabChildProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pages, setPages] = useState<LedgerEntry[][]>([])
  const query = useFetchPayoutsLedger({ page: currentPage, limit: PAGE_LIMIT })

  useEffect(() => {
    if (query.isLoading) return
    setPages((prev) => {
      const next = [...prev]
      next[currentPage - 1] = query.data
      return next
    })
  }, [query.data, query.isLoading, currentPage])

  const entries = pages.flat()
  const isFirstLoad = pages.length === 0 && query.isLoading

  return (
    <TabBody
      entries={entries}
      isFirstLoad={isFirstLoad}
      isFetchingMore={query.isFetching && currentPage > pages.length}
      hasMore={query.meta.hasMore}
      onLoadMore={() => setCurrentPage((p) => p + 1)}
    />
  )
}

type TabBodyProps = {
  entries: LedgerEntry[]
  isFirstLoad: boolean
  isFetchingMore: boolean
  hasMore: boolean
  onLoadMore: () => void
}

const TabBody = ({ entries, isFirstLoad, isFetchingMore, hasMore, onLoadMore }: TabBodyProps) => {
  if (isFirstLoad) {
    return (
      <div className="flex w-full justify-center py-10">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <LedgerList entries={entries} />
      {entries.length > 0 && hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="bordered-gray" size="small" onClick={onLoadMore} isLoading={isFetchingMore}>
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}
    </div>
  )
}

export const TransactionsPanel = () => {
  const { t } = useLingui()
  const [activeTab, setActiveTab] = useState<TabValue>('ALL')
  const [hasSeenAdjustment, setHasSeenAdjustment] = useState(false)

  const onSeenAdjustment = useCallback(() => setHasSeenAdjustment(true), [])

  const tabs: { title: string; value: TabValue }[] = [
    { title: t`All`, value: 'ALL' },
    { title: t`Earnings`, value: 'EARNINGS' },
    { title: t`Payouts`, value: 'PAYOUTS' },
    ...(hasSeenAdjustment ? [{ title: t`Other`, value: 'OTHER' as TabValue }] : [])
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="max-mobile:flex-col max-mobile:items-stretch flex items-center justify-between gap-4">
        <div className="text-subtitle font-semibold">
          <Trans>Transactions</Trans>
        </div>
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={(tab) => setActiveTab(tab.value)}
          size="small"
          className="max-w-[420px]"
        />
      </div>

      {activeTab === 'PAYOUTS' ? (
        <PayoutsTab key="payouts" onSeenAdjustment={onSeenAdjustment} />
      ) : activeTab === 'ALL' ? (
        <LedgerTab key="all" onSeenAdjustment={onSeenAdjustment} />
      ) : (
        <LedgerTab key={activeTab} kind={TAB_KIND_MAP[activeTab]} onSeenAdjustment={onSeenAdjustment} />
      )}
    </div>
  )
}
